import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Providers
import PreloadProvider from './components/providers/PreloadProvider';
import { BackgroundProvider } from './contexts/BackgroundContext';
import { ErrorNotificationProvider } from './contexts/ErrorNotificationContext';
import CSRFProvider from './components/security/CSRFProvider';
import { UserInteractionProvider } from './components/user-interaction';

// Monitoring
import { monitoringService } from './services/monitoring';
import { monitoringConfig } from './config/monitoring';

// Layouts
import MainAppLayout from './layouts/MainAppLayout';
import WorkbenchLayout from './layouts/WorkbenchLayout';

// Page Components
import AuthPage from './features/auth/components/AuthPage';
import { PasswordResetConfirm } from './features/auth/components';
import GlobalBackground from './components/common/GlobalBackground';
import BackgroundControls from './components/common/BackgroundControls';
import EasterEggDetector from './components/common/EasterEggDetector';
import { GlobalErrorBoundary } from './components/common/ErrorBoundary';
// Lazy-loaded heavy components
const AutoPilotDashboard = lazy(() => import('./components/autopilot/AutoPilotDashboard'));
const CopilotDashboard = lazy(() => import('./components/copilot/CopilotDashboard'));
const ETLWorkbench = lazy(() => import('./components/workbench/ETLWorkbench'));
const JobMonitorScreen = lazy(() => import('./components/job-monitor/JobMonitorScreen'));
// Unused imports - components moved to unused/ directory
// import PlatformDashboard from './components/unused/platforms/PlatformDashboard';
// import JobTypesDashboard from './components/unused/jobtypes/JobTypesDashboard';
// import EditJobType from './components/unused/jobtypes/EditJobType';
// import JobsDashboard from './components/unused/jobs/JobsDashboard';
// import JobRunnerPage from './components/unused/jobs/JobRunnerPage';
// import EndpointsDashboard from './components/unused/endpoints/EndpointsDashboard';
// import ConfigurationsDashboard from './components/unused/configurations/ConfigurationsDashboard';
// import SsotDashboard from './components/unused/ssot/SsotDashboard';
// import NodeEditor from './components/unused/flow/NodeEditor'; // Assuming this is used for transform/load pages

function App() {
  // Initialize monitoring services
  useEffect(() => {
    // Disable console logs in production builds
    if (import.meta.env.PROD) {
      const noop = () => {};
      console.log = noop;
      console.info = noop;
      console.debug = noop;
      // Keep console.warn and console.error for important issues
    }

    monitoringService.initialize(monitoringConfig);
    
    // Track app initialization
    monitoringService.trackEvent('app_initialized', {
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  }, []);

  return (
    <GlobalErrorBoundary>
      <CSRFProvider>
        <ErrorNotificationProvider position="top-right" maxNotifications={5}>
          <BackgroundProvider>
            <UserInteractionProvider>
              <PreloadProvider>
                <GlobalBackground />
                <BackgroundControls />
                <EasterEggDetector />
              <Routes>
            {/*
              PUBLIC ROUTES - These routes do not require authentication

              Security considerations:
              - These routes are intentionally public for user account recovery and access
              - All sensitive operations require additional validation beyond just route access
              - Rate limiting is enforced at the API level to prevent abuse
            */}

            {/* Authentication page - allows users to login/register */}
            <Route path="/auth" element={<AuthPage />} />

            {/*
              Password reset confirmation - MUST be public for security reasons:

              1. Users cannot authenticate if they've forgotten their password
              2. The security is enforced through:
                 - Server-side token validation with expiration
                 - One-time use tokens that are invalidated after use
                 - Tokens are cryptographically secure and unpredictable
                 - Rate limiting prevents brute force attacks
              3. The token in the URL is necessary for email link functionality
              4. Browser history exposure is mitigated by:
                 - Short token lifespan (typically 15-30 minutes)
                 - One-time use invalidation
                 - Server-side validation of token authenticity
            */}
            <Route path="/reset-password/:token" element={<PasswordResetConfirm />} />

            {/* Protected routes - these will use MainAppLayout */}
            {/* <Route element={<ProtectedRoute />}> */}
              <Route element={<MainAppLayout />}>
                <Route path="/" element={<Navigate to="/autopilot" replace />} />
                <Route path="/autopilot" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
                    <AutoPilotDashboard />
                  </Suspense>
                } />
                 <Route path="/copilot" element={
                   <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
                     <CopilotDashboard />
                   </Suspense>
                 } />
                <Route path="/job-monitor" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
                    <JobMonitorScreen />
                  </Suspense>
                } />
                {/* Unused routes - moved to unused/ directory */}
                {/* <Route path="/platforms" element={<PlatformDashboard />} />
                <Route path="/job-types" element={<JobTypesDashboard />} />
                <Route path="/job-types/edit/:jobTypeId" element={<EditJobType />} />
                <Route path="/jobs" element={<JobsDashboard />} />
                <Route path="/job-runner" element={<JobRunnerPage />} />
                <Route path="/endpoints" element={<EndpointsDashboard />} />
                <Route path="/endpoints/:jobTypeId" element={<EndpointsDashboard />} />
                <Route path="/configurations" element={<ConfigurationsDashboard />} />
                <Route path="/ssot" element={<SsotDashboard />} />
                <Route path="/transform" element={<NodeEditor />} />
                <Route path="/load" element={<NodeEditor />} /> */}
                {/* Add other protected routes here, they will inherit the MainAppLayout */}
              </Route>
            {/* </Route> */}

            {/* Workbench routes with special layout */}
            {/* <Route element={<ProtectedRoute />}> */}
              <Route element={<WorkbenchLayout />}>
                <Route path="/workbench" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
                    <ETLWorkbench />
                  </Suspense>
                } />
              </Route>
            {/* </Route> */}

            {/* Optional: Catch-all route for 404 Not Found pages */}
            {/* <Route path="*" element={<NotFoundPage />} /> */}
              </Routes>
              </PreloadProvider>
            </UserInteractionProvider>
          </BackgroundProvider>
        </ErrorNotificationProvider>
      </CSRFProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
